<script setup>
import {computed, defineAsyncComponent, ref} from 'vue';
import {useStore} from 'vuex';
import FygrSelector from '@/components/elements/FygrSelector.vue';
import CategoriesDropdownRectangle from '@/components/elements/CategoriesDropdownRectangle.vue';
const store = useStore();
const emit = defineEmits(['close', 'confirmed', 'confirm']);

const DefaultMobileFrame = defineAsyncComponent(
  () => import('@/components/elements/DefaultMobileFrame.vue'),
);
const MobileElementSelector = defineAsyncComponent(
  () => import('@/components/elements/MobileElementSelector.vue'),
);
const FygrAccountSelect = defineAsyncComponent(
  () => import('@/components/elements/FygrAccountSelect.vue'),
);
const props = defineProps({
  mobile: {
    type: Boolean,
    default: () => false,
  },
  deletedType: {
    type: String,
    default: () => 'account',
  },
  deletedId: {
    type: String,
    default: () => null,
  },
});
const accountsDDOptions = computed(() => {
  const options = store.getters['bank/accountsDDOptions'] || [];
  const deletedId = props.deletedId;
  const deletedType = props.deletedType;

  return options
    .map((bank) => {
      if (!bank) return false;
      if (deletedType === 'account') {
        return {...bank, list: bank.list.filter((itm) => itm.id !== deletedId)};
      } else {
        return bank;
      }
    })
    .filter((bnk) => {
      if (deletedType === 'account') {
        return bnk;
      } else {
        return bnk.id !== deletedId;
      }
    });
});
const alternativeAccountsAvailable =
  accountsDDOptions.value.map((itm) => itm.list).flat(2).length > 0;

const accountsDDOptionsMobile = accountsDDOptions.value
  .map((itm) => itm.list)
  .flat(2);
const picked = ref('deactivate');
const page = ref('main');
const Dropdown = ref(null);
const newDefaultAccountId = ref(null);
const deleting = ref(false);

async function validateSelection() {
  if (page.value === 'main') {
    if (picked.value === 'modify') {
      page.value = 'modifyAccount';
      return;
    } else {
      deleting.value = true;
      await store
        .dispatch('bank/actionSetAccountLevelForecastingActivated', {
          is_activated: false,
        })
        .then(() =>
          store.dispatch(
            'user/actionGetCompanyInfo',
            localStorage.getItem('FYGR_COMPANY'),
          ),
        )
        .finally((deleting.value = false));

      emit('confirm');
    }
  }
  if (page.value === 'modifyAccount') {
    deleting.value = true;
    await store
      .dispatch('bank/actionSetDefaultForecastingAccount', {
        account_id: newDefaultAccountId.value,
      })
      .then(() =>
        store.dispatch(
          'user/actionGetCompanyInfo',
          localStorage.getItem('FYGR_COMPANY'),
        ),
      )
      .finally((deleting.value = false));
    emit('confirm');
  }
}
function selectAccount(id) {
  newDefaultAccountId.value = id;
  Dropdown.value = null;
}
function selectAccountMobile(account) {
  if (account.parentId) {
    newDefaultAccountId.value = account.id;
    validateSelection();
  }
}
</script>
<template>
  <CategoriesDropdownRectangle
    v-if="Dropdown"
    :event-container="Dropdown"
    :list="Dropdown.list"
    forecast-enabled-only
    :exclude-translation="true"
    disable-adding
    deepest
    only-alterable
    variable
    @close="Dropdown = null"
    @select="selectAccount"
  />
  <BasePopup
    v-if="!props.mobile"
    title="Mettre à jour le compte bancaire par défaut"
    close-button
    footer-required
    advanced-title
    @close="$emit('close', $event)"
  >
    <div
      v-if="page === 'main'"
      class="dc-wrap"
    >
      <div class="dc-text text-gray translate">
        Le prévisionnel compte par compte est activé pour votre société et vous
        tentez actuellement de supprimer le compte par défaut
        <FygrSelector
          ref="selector"
          class="selector"
          margin-bottom="18px"
          :diabled-items="alternativeAccountsAvailable ? [] : [0]"
          :opts="[
            'Modifier le compte bancaire par défaut',
            'Désactiver le module de prévisionnel compte par compte et conserver le prévisionnel tel quel',
          ]"
          :model-value="picked === 'modify' ? 0 : 1"
          @update:model-value="picked = $event === 0 ? 'modify' : 'deactivate'"
        />
        <slot />
      </div>
    </div>
    <div v-if="page === 'modifyAccount'">
      <FygrAccountSelect
        :account-selected="newDefaultAccountId"
        :width="0"
        :accounts-list="accountsDDOptions"
        use-default-account
        label="Compte Bancaire par défaut"
        @selected="selectAccount($event)"
      />
    </div>
    <template #lheader>
      <div
        v-if="page === 'modifyAccount'"
        class="fy-aip-return clickable"
        @click="page = 'main'"
      >
        <i class="fi-back" />
      </div>
    </template>
    <template #footer>
      <div class="dc-controls">
        <FygrButton
          class="dc-confirm"
          :disabled="false"
          :loading="deleting"
          @fygr-click="validateSelection()"
        >
          Valider
        </FygrButton>
        <a
          class="text-aubergine clickable translate"
          @click="$emit('close')"
        >
          Annuler
        </a>
      </div>
    </template>
  </BasePopup>
  <DefaultMobileFrame
    v-if="props.mobile"
    ref="delbank"
    title="Mettre à jour le compte bancaire par défaut"
    @close="emit('close')"
  >
    <div
      v-if="page === 'main'"
      class="fy-app-dmf-container"
    >
      <div class="descr text-gray translate">
        Le prévisionnel compte par compte est activé pour votre société et vous
        tentez actuellement de supprimer le compte par défaut
      </div>
      <FygrSelector
        ref="selector"
        class="selector"
        margin-bottom="18px"
        :opts="[
          'Modifier le compte bancaire par défaut',
          'Désactiver le module de prévisionnel compte par compte et conserver le prévisionnel tel quel',
        ]"
        :model-value="picked === 'modify' ? 0 : 1"
        @update:model-value="picked = $event === 0 ? 'modify' : 'deactivate'"
      />
      <div class="fy-app-dmf-container-controls">
        <FygrButton
          class="fy-app-dmf-container-confirm"
          :loading="deleting"
          @fygr-click="validateSelection()"
        >
          Supprimer
        </FygrButton>
        <a
          class="fy-app-dmf-container-cancel clickable translate"
          @click="$emit('close')"
        >
          Annuler
        </a>
      </div>
    </div>
    <MobileElementSelector
      v-if="page === 'modifyAccount'"
      class="dd"
      placeholder="Recherche..."
      :elements="accountsDDOptionsMobile"
      capitalize
      show-parent
      @select="selectAccountMobile"
    />
  </DefaultMobileFrame>
</template>
<script></script>
<style lang="scss" scoped>
@use '@assets/styleMixins/accountStyleMixin';
.acc-dd-wrap {
  text-align: left;
  width: 100%;
  .fy-acc-dropdown {
    width: auto;
  }
}
.dc-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: var(--px350);
  width: var(--px500);
  .selector {
    margin-top: 40px;
    text-align: left;
  }
  &.small-width {
    width: 282px;
    min-width: 282px;
    flex: 0;
    .dc-text {
      margin: 0;
    }
  }
  .dc-title {
    margin-bottom: var(--px60);
  }
  .dc-text {
    margin: 0 var(--px30);
  }
  .dc-input {
    margin-top: var(--px40);
    width: var(--px280);
  }
}
.dc-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  .dc-confirm {
    padding: 0 var(--px30);
    height: var(--px32);
    margin-bottom: var(--px15);
  }
}
.fy-aip-return {
  color: $gray;
  position: absolute;
  font-size: var(--px20);
  top: var(--px5);
  left: 0;
}
.fy-app-dmf-container {
  .dd {
    margin-top: 30px;
  }
  .descr {
    margin: 20px 0 40px 0;
  }
  .selector {
    margin-bottom: 40px;
    text-align: left;
  }
}
</style>
